/**
 * Pure layout-tier thresholds for the agent selection bar.
 *
 * Toolbar width uses getBoundingClientRect only (see useSelectionBarLayoutTier). Control-label
 * measurement in useSelectionBarControlWidths uses max(rect, scrollWidth) for truncated scope text.
 */

/** Calibration anchors — progressive tuck order. */
export const SELECTION_BAR_HIDE_THUMBS_PX = 640;
export const SELECTION_BAR_HIDE_SCOPE_PX = 760;
export const SELECTION_BAR_COMPACT_CTA_PX = 480;
export const SELECTION_BAR_HIDE_PREFIX_PX = 440;

/** Icon CTAs when showcase share is on the bar — shell-visible anchor minus Start task chrome. */
export const SELECTION_BAR_SHELL_CHROME_RESERVE_PX = 160;
export const SELECTION_BAR_SHELL_COMPACT_CTA_WITH_SHOWCASE_PX = 740;
export const SELECTION_BAR_SHELL_HIDE_THUMBS_WITH_SHOWCASE_PX = 860;
export const SELECTION_BAR_COMPACT_CTA_WITH_SHOWCASE_PX =
  SELECTION_BAR_SHELL_COMPACT_CTA_WITH_SHOWCASE_PX - SELECTION_BAR_SHELL_CHROME_RESERVE_PX;
export const SELECTION_BAR_HIDE_THUMBS_WITH_SHOWCASE_PX =
  SELECTION_BAR_SHELL_HIDE_THUMBS_WITH_SHOWCASE_PX - SELECTION_BAR_SHELL_CHROME_RESERVE_PX;

/**
 * Reference row the 760px scope band was tuned against (`Select all 5 items`, `1 item`, ` · My Board ▾`).
 * `itemCountMinPx` is count+chevron when scope is tucked; count-only when scope is visible.
 * Live scope measure includes the dropdown chevron when it sits on the scope side.
 */
export const SELECTION_BAR_SCOPE_BAND_REFERENCE = {
  selectAllButtonPx: 118,
  itemCountMinPx: 54,
  itemCountScopePx: 30,
} as const;

/** Input shape for getScopeBandReservesFromWidths (maps itemCountScopePx → scopePx). */
export const SCOPE_BAND_REFERENCE_WIDTHS = {
  itemCountMinPx: SELECTION_BAR_SCOPE_BAND_REFERENCE.itemCountMinPx,
  scopePx: SELECTION_BAR_SCOPE_BAND_REFERENCE.itemCountScopePx,
  selectAllPx: SELECTION_BAR_SCOPE_BAND_REFERENCE.selectAllButtonPx,
} as const;

/** One Tailwind `gap-2` step in the left group (8px). */
export const SELECTION_BAR_LEFT_GROUP_GAP_PX = 8;

/** Two `gap-2` steps between count, spacer, and select-all (n siblings → n − 1 gaps). */
export const SELECTION_BAR_LEFT_GROUP_GAP_COUNT = 2;

/** Flex-collapse slack beyond measured control widths (prior baseline was 20px for two gaps + slack). */
export const SELECTION_BAR_SELECT_ALL_BAND_SLACK_PX = 4;

/**
 * Left-group flex gaps between count, spacer, and select-all — not captured in per-control measure.
 * Two `gap-2` (16px) plus 4px flex-collapse slack (20px total).
 */
export const SELECTION_BAR_SELECT_ALL_BAND_CLEARANCE_PX =
  SELECTION_BAR_LEFT_GROUP_GAP_PX * SELECTION_BAR_LEFT_GROUP_GAP_COUNT + SELECTION_BAR_SELECT_ALL_BAND_SLACK_PX;

/** Deadband so tuck flags do not flip when live measures shift thresholds by a few pixels. */
export const SELECTION_BAR_TUCK_HYSTERESIS_PX = 4;

/** gap-1.5 (6px) + ChevronDown size-4 (16px) beside count when scope is tucked. */
export const SELECTION_BAR_ITEM_COUNT_CHEVRON_RESERVE_PX = 22;

export type SelectionBarLayoutTierReserves = {
  compactCtaThresholdPx?: number;
  itemCountMinReservePx?: number;
  itemCountScopeReservePx?: number;
  selectAllBandClearancePx?: number;
  selectAllReservePx?: number;
  thumbThresholdPx?: number;
};

export type SelectionBarLayoutTierFlags = {
  /** Icon-only Download/Share — only when truly tight. */
  compactOutlineCtAs: boolean;
  /** Prefix icons → ⋮ last resort. */
  hidePrefixActions: boolean;
  hideThumbStrip: boolean;
  hideScopeLabel: boolean;
  hideSelectAllButton: boolean;
  hideIncludeNestedButton: boolean;
};

export const areSelectionBarLayoutTierFlagsEqual = (
  left: SelectionBarLayoutTierFlags,
  right: SelectionBarLayoutTierFlags,
): boolean =>
  left.compactOutlineCtAs === right.compactOutlineCtAs &&
  left.hideIncludeNestedButton === right.hideIncludeNestedButton &&
  left.hidePrefixActions === right.hidePrefixActions &&
  left.hideScopeLabel === right.hideScopeLabel &&
  left.hideSelectAllButton === right.hideSelectAllButton &&
  left.hideThumbStrip === right.hideThumbStrip;

export type ScopeBandReserveWidths = Pick<
  SelectionBarLayoutTierReserves,
  'itemCountMinReservePx' | 'itemCountScopeReservePx' | 'selectAllReservePx'
>;

export const getCompactCtaThresholdPx = (hasShowcaseShare: boolean): number => {
  if (hasShowcaseShare) {
    return SELECTION_BAR_COMPACT_CTA_WITH_SHOWCASE_PX;
  }

  // Start task sits inside the measured bar (`endCta`), so do not subtract the old FAB chrome
  // reserve — that dropped compact to 320px while prefix still tucks at 440px.
  return SELECTION_BAR_COMPACT_CTA_PX;
};

export const getThumbThresholdPx = (hasShowcaseShare: boolean): number => {
  if (hasShowcaseShare) {
    return SELECTION_BAR_HIDE_THUMBS_WITH_SHOWCASE_PX;
  }

  return SELECTION_BAR_HIDE_THUMBS_PX;
};

export const getScopeBandReservesFromWidths = (
  widths: {
    itemCountMinPx: number;
    scopePx: number;
    selectAllPx: number;
  },
  willOfferSelectAll: boolean,
  hasScopeLabel: boolean,
): ScopeBandReserveWidths => ({
  itemCountMinReservePx: Math.max(0, widths.itemCountMinPx - SELECTION_BAR_SCOPE_BAND_REFERENCE.itemCountMinPx),
  itemCountScopeReservePx: hasScopeLabel
    ? Math.max(0, widths.scopePx - SELECTION_BAR_SCOPE_BAND_REFERENCE.itemCountScopePx)
    : 0,
  selectAllReservePx: willOfferSelectAll
    ? Math.max(0, widths.selectAllPx - SELECTION_BAR_SCOPE_BAND_REFERENCE.selectAllButtonPx)
    : 0,
});

export const buildScopeBandLayoutReserves = (
  scopeBandReserves: ScopeBandReserveWidths,
  willOfferSelectAll: boolean,
): ScopeBandReserveWidths & Pick<SelectionBarLayoutTierReserves, 'selectAllBandClearancePx'> => ({
  ...scopeBandReserves,
  selectAllBandClearancePx: willOfferSelectAll ? SELECTION_BAR_SELECT_ALL_BAND_CLEARANCE_PX : 0,
});

export const getHideSelectAllThresholdPx = (reserves: SelectionBarLayoutTierReserves): number =>
  SELECTION_BAR_HIDE_SCOPE_PX +
  (reserves.selectAllReservePx ?? 0) +
  (reserves.itemCountMinReservePx ?? 0) +
  (reserves.selectAllBandClearancePx ?? 0);

export const getHideScopeThresholdPx = (reserves: SelectionBarLayoutTierReserves): number =>
  getHideSelectAllThresholdPx(reserves) + (reserves.itemCountScopeReservePx ?? 0);

const isBelowTuckThreshold = (toolbarWidth: number, thresholdPx: number, wasTucked: boolean): boolean =>
  wasTucked ? toolbarWidth < thresholdPx + SELECTION_BAR_TUCK_HYSTERESIS_PX : toolbarWidth < thresholdPx;

export const layoutTierFlags = (
  toolbarWidth: number | null = null,
  reserves: SelectionBarLayoutTierReserves = {},
  isPhone = false,
  previousFlags?: SelectionBarLayoutTierFlags,
): SelectionBarLayoutTierFlags => {
  const w = toolbarWidth ?? Number.POSITIVE_INFINITY;
  const compactCtaThreshold = reserves.compactCtaThresholdPx ?? SELECTION_BAR_COMPACT_CTA_PX;
  const thumbThreshold = reserves.thumbThresholdPx ?? SELECTION_BAR_HIDE_THUMBS_PX;
  const hideSelectAllThreshold = getHideSelectAllThresholdPx(reserves);
  const hideScopeThreshold = getHideScopeThresholdPx(reserves);
  const wasScopeTucked = previousFlags?.hideScopeLabel ?? false;
  const wasSelectAllTucked = previousFlags?.hideSelectAllButton ?? false;

  return {
    compactOutlineCtAs: isPhone || w < compactCtaThreshold,
    hidePrefixActions: isPhone || w < SELECTION_BAR_HIDE_PREFIX_PX,
    hideThumbStrip: isPhone || w < thumbThreshold,
    hideScopeLabel: isPhone || isBelowTuckThreshold(w, hideScopeThreshold, wasScopeTucked),
    hideSelectAllButton: isPhone || isBelowTuckThreshold(w, hideSelectAllThreshold, wasSelectAllTucked),
    hideIncludeNestedButton: isPhone || isBelowTuckThreshold(w, hideSelectAllThreshold, wasSelectAllTucked),
  };
};

/** Wide-layout defaults — stable reference for hook default parameters. */
export const DEFAULT_SELECTION_BAR_LAYOUT_TIER_FLAGS: SelectionBarLayoutTierFlags = layoutTierFlags();

export const getAgentSelectionBarLayoutTierAttribute = (
  flags: SelectionBarLayoutTierFlags,
): 'wide' | 'compact-ctas' | 'tucked' => {
  if (flags.hidePrefixActions) {
    return 'tucked';
  }
  if (flags.compactOutlineCtAs) {
    return 'compact-ctas';
  }

  return 'wide';
};
